// Lib
import React, { ReactElement, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import Helmet from 'react-helmet';
import { compose } from '../../node_module_clones/recompose';
import { hot } from 'react-hot-loader/root';

// Utils
import { ZoomStateContextProvider } from '../canvas/zoom/ZoomStateContext';
import platformSingleton from '../platform/platformSingleton';
import { getRootType } from '../../common/elements/utils/elementPropertyUtils';
import { getEmailIsVerified, isFeatureEnabled } from '../../common/users/utils/userPropertyUtils';

// Selectors
import { selectIsMobileLandscapeModeAllowed } from '../platform/screenOrientation/mobileScreenOrientationSelector';

// Actions
import { deselectAllElements } from '../element/selection/selectionActions';
import { closeSheet } from './structural/sheet/sheetActions';

// Hooks
import useAttachNotifications from '../capacitor/hooks/useAttachNotifications';
import ScrollToCaretHandler from './boardsPage/editing/ScrollToCaretHandler';
import useVirtualKeyboardEventHandler from '../utils/keyboard/useVirtualKeyboardEventHandler';
import useFetchHistoryOnMount from '../workspace/header/workspaceToolsHeader/boardSearch/state/useFetchHistoryOnMount';
import useFetchQuickNotesOnMount from './utils/useFetchQuickNotesOnMount';
import { useMobileWorkspaceRestoreMode } from './utils/mobileWorkspaceRestoreMode';
import useShowMobileAppReviewPrompt from './useShowMobileAppReviewPrompt';

// Components
import MobileToolbar from './toolbar/MobileToolbar';
import MobileKeyboardAnimationDebugPanel from './debug/MobileKeyboardAnimationDebugPanel';
import MobileCanvasContextMenu from './contextMenu/MobileCanvasContextMenu';
import MobileBoardHeader from './boardsPage/header/MobileBoardHeader';
import MobilePageBody from './structural/MobilePageBody';
import WorkspacePositioner from '../workspace/WorkspacePositioner';
import WorkspaceClipboardHandler from '../workspace/shortcuts/clipboard/WorkspaceClipboardHandler';
import GlobalDragAndDropHelper from '../utils/dnd/GlobalDragAndDropHelper';
import GlobalPinchGestureBlocker from '../utils/ipad/GlobalPinchGestureBlocker';
import CurrentUserSubscriptionSynchroniser from '../user/CurrentUserSubscriptionSynchroniser';
import AnnotationEditor from '../element/drawing/annotation/annotationEditor/LoadableAnnotationEditor';
import WorkspaceTooltipManager from '../components/tooltips/WorkspaceTooltipManager';
import Main from '../workspace/main/Main';
import MobileNotificationsSheet from './notifications/MobileNotificationsSheet';
import MobileQuickNotesPage from '../workspace/quickNotes/MobileQuickNotesPage';
import BillingAlertSheet from './billing/BillingAlertSheet';
import MobileSharingSheet from './sharing/MobileSharingSheet';
import WorkspaceOnboardingManager from '../workspace/onboarding/WorkspaceOnboardingManager';
import LoadableMobileSearchSheet from './toolbar/tools/searchTool/LoadableMobileSearchSheet';
import MobileNotificationsReconciler from './notifications/MobileNotificationsReconciler';
import ReportContentSheet from '../workspace/contentReport/ReportContentSheet';
import LoadableEmailVerificationRequiredSheet from '../user/account/emailVerification/LoadableEmailVerificationRequiredSheet';
import MobileGestureShakeContextMenu from './gesture/MobileGestureShakeContextMenu';

import elementShortcutsDecorator from '../workspace/shortcuts/clipboard/elementShortcutsDecorator';
import MobileDebug from '../utils/keyboard/MobileDebug';
import MobileBoardTemplatePickerSheet from '../workspace/boardTemplatePickerPopup/MobileBoardTemplatePickerSheet';
import ListPlaceholderContextProvider from '../element/elementPlaceholder/ListPlaceholderContextProvider';

// Types
import { GeneralWorkspaceProps } from '../workspace/workspaceTypes';
import { RootBoardTypes } from '../../common/boards/boardConstants';
import { SheetId } from './structural/sheet/sheetTypes';

// Styles
import './mobile-ui-variables.scss';
import './MobileWorkspace.scss';
import './MobileWorkspaceLandscape.scss';
import '../workspace/Workspace.scss';
import { ExperimentId } from '../../common/experiments/experimentsConstants';
import useCapacitorTextZoomLevel from './utils/useCapacitorTextZoomLevel';
import MobileNavigationBar from './structural/MobileNavigationBar';

const MobileWorkspace = (props: GeneralWorkspaceProps): ReactElement => {
    const { currentBoard, breadcrumbObj, currentUser } = props;

    const toolbarRef = useRef<HTMLDivElement>(null);
    const mobileWorkspaceRef = useRef<HTMLDivElement>(null);

    const isLandscapeAllowed = useSelector(selectIsMobileLandscapeModeAllowed);
    const shouldShowMobileTemplatePicker = isFeatureEnabled(ExperimentId.mobileTemplatePicker, currentUser);

    const isDevMode = process.env.NODE_ENV === 'development';
    const { isCurrentBoardRoot, isCurrentBoardCached } = props;

    // If the current board is cached - don't show a loader or canvas contents
    const mainComponent = !isCurrentBoardCached ? <Main {...props} /> : null;

    const dispatch = useDispatch();
    // Allow clicking outside the screen to deselect elements in dev mode
    //  -- not actually reachable in the production app
    const dispatchDeselectAll = () => dispatch(deselectAllElements());

    const onWorkspaceClick = (event: React.MouseEvent) => {
        if (mobileWorkspaceRef.current !== event.target) return;
        dispatchDeselectAll();
    };

    const isCurrentBoardQuickNotes = getRootType(currentBoard) === RootBoardTypes.QUICK_NOTES;

    // Set the restore mode based on the current board type
    useMobileWorkspaceRestoreMode(isCurrentBoardQuickNotes);

    // Listen for mobile push notification registrations
    useAttachNotifications();

    // Listen for keyboard events
    useVirtualKeyboardEventHandler();

    // Fetch history on workspace mount to improve recently viewed speed
    useFetchHistoryOnMount();

    // Fetch quick notes on workspace mount to improve speed of quick notes navigation
    useFetchQuickNotesOnMount();

    // Reset the text zoom scale to 1 when the workspace is mounted
    useCapacitorTextZoomLevel();

    // Show the mobile app review prompt if conditions are met
    useShowMobileAppReviewPrompt();

    return (
        <div
            className={classNames('MobileWorkspaceWrapper', {
                'dev-mode': isDevMode,
                'disable-landscape': !isLandscapeAllowed,
            })}
        >
            <div
                className={classNames('MobileWorkspace', 'Workspace', 'workspace-ready', {
                    'workspace-root-board': isCurrentBoardRoot,
                })}
                onClick={onWorkspaceClick}
                ref={mobileWorkspaceRef}
            >
                <Helmet>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=no viewport-fit=cover"
                    />
                </Helmet>
                <ScrollToCaretHandler mobileToolbarRef={toolbarRef} />
                <ZoomStateContextProvider zoomScale={1}>
                    <MobileBoardHeader currentBoard={currentBoard} breadcrumbObj={breadcrumbObj} />

                    {!isCurrentBoardQuickNotes && (
                        <MobilePageBody>
                            <WorkspacePositioner {...props}>{mainComponent}</WorkspacePositioner>
                            <AnnotationEditor />
                        </MobilePageBody>
                    )}

                    <ListPlaceholderContextProvider>
                        {isCurrentBoardQuickNotes && <MobileQuickNotesPage {...props} />}
                    </ListPlaceholderContextProvider>

                    <MobileToolbar
                        {...props}
                        hasEditorSelection={false /* just passing this to stop TS from complaining */}
                        toolbarRef={toolbarRef}
                    />

                    <MobileNotificationsSheet />
                    {shouldShowMobileTemplatePicker && <MobileBoardTemplatePickerSheet currentBoard={currentBoard} />}
                    <LoadableMobileSearchSheet />

                    <MobileSharingSheet {...props} />

                    <MobileKeyboardAnimationDebugPanel mobileWorkspaceRef={mobileWorkspaceRef} />
                    <MobileCanvasContextMenu
                        workspaceRef={mobileWorkspaceRef}
                        beforeSheetOpen={() => dispatch(closeSheet(SheetId.Inbox))}
                    />

                    <MobileGestureShakeContextMenu />

                    <BillingAlertSheet />
                    <ReportContentSheet />
                    {!getEmailIsVerified(currentUser) && <LoadableEmailVerificationRequiredSheet />}

                    <WorkspaceClipboardHandler {...props} />
                    <GlobalDragAndDropHelper />
                    {platformSingleton.features.isTouch ? <GlobalPinchGestureBlocker /> : null}
                    <CurrentUserSubscriptionSynchroniser />

                    <WorkspaceOnboardingManager />

                    <WorkspaceTooltipManager />
                    <MobileNotificationsReconciler />
                    <MobileDebug />
                </ZoomStateContextProvider>
            </div>
            <MobileNavigationBar />
        </div>
    );
};

const enhance = compose<GeneralWorkspaceProps, GeneralWorkspaceProps>(hot, elementShortcutsDecorator);

export default enhance(MobileWorkspace);
